import React, { useEffect, useMemo, useState } from "react"

import { useTranslation } from "react-i18next"

import { TablePagination } from "components/paginations/TablePagination"
import { Column, DataTable } from "components/tables/DataTable"

import { PromoCodeBonus } from "services/bonus/type"

import { getBonusTypeColor, getDisplayName } from "../../helpers/bonus2.helpers"
import { downloadPromoCodeRowsCsv } from "../../helpers/promo-code.helpers"

interface Props {
  promoCodes: PromoCodeBonus[]
  onViewUsage: (promoCode: PromoCodeBonus) => void
  onToggle: (promoCode: PromoCodeBonus, activate: boolean) => void
  onArchive: (promoCode: PromoCodeBonus, archived: boolean) => void
  onDelete: (promoCode: PromoCodeBonus) => void
}

const GROUPS_PER_PAGE = 10

const pill = "px-2 py-1 text-xs rounded text-white"
const action = "px-3 py-1 text-xs text-white rounded transition-all"

const codeIsOn = (p: PromoCodeBonus) => p.codeActive && !p.codeArchived

/** Redemption needs the code AND its bonus to be live, and the two are switched independently. */
const bonusState = (p: PromoCodeBonus): { label: string; className: string } => {
  if (p.archived) return { label: "bonus2.promo.bonusArchived", className: "bg-slate-600" }
  if (!p.active) return { label: "bonus2.promo.bonusInactive", className: "bg-amber-600" }
  return { label: "bonus2.promo.bonusLive", className: "bg-emerald-600" }
}

export const PromoCodeTable = ({ promoCodes, onViewUsage, onToggle, onArchive, onDelete }: Props) => {
  const { t } = useTranslation("translation", { useSuspense: false })
  const [page, setPage] = useState(1)
  const [openIds, setOpenIds] = useState<string[]>([])

  const groups = useMemo(() => {
    const out: PromoCodeBonus[][] = []
    const byBonus = new Map<string, PromoCodeBonus[]>()
    promoCodes.forEach((p) => {
      const existing = byBonus.get(p.bonus_id)
      if (existing) {
        existing.push(p)
        return
      }
      const group = [p]
      byBonus.set(p.bonus_id, group)
      out.push(group)
    })
    return out
  }, [promoCodes])

  const totalPages = Math.max(1, Math.ceil(groups.length / GROUPS_PER_PAGE))
  const safePage = Math.min(page, totalPages)
  const visible = groups.slice((safePage - 1) * GROUPS_PER_PAGE, safePage * GROUPS_PER_PAGE)
  const firstId = visible.length > 0 ? visible[0][0].bonus_id : undefined

  useEffect(() => {
    setOpenIds(firstId ? [firstId] : [])
  }, [firstId])

  const columns: Column<PromoCodeBonus>[] = [
    {
      header: t("bonus2.promo.thCode"),
      width: "20%",
      render: (p) => (
        <span className={p.codeArchived ? "opacity-60" : undefined}>
          <span className="font-mono font-semibold">{p.promo_code}</span>
          {p.codeArchived && <span className="ml-2 text-xs text-gray-400">({t("bonus2.promo.archived")})</span>}
        </span>
      )
    },
    {
      header: t("bonus2.promo.thType"),
      width: "12%",
      render: (p) => <span className={`${pill} ${getBonusTypeColor(p.type)}`}>{p.type}</span>
    },
    {
      header: t("bonus2.promo.thRedemptions"),
      width: "20%",
      render: (p) => (
        <div>
          <div>
            {p.redemptionCount} {t("bonus2.promo.viaThisCode")}
          </div>
          <div className="text-xs text-gray-500">{t("bonus2.promo.onTheBonus", { count: p.bonusRedemptionCount })}</div>
        </div>
      )
    },
    {
      header: t("bonus2.promo.thStatus"),
      width: "16%",
      render: (p) => {
        const bonus = bonusState(p)
        return (
          <div className="flex flex-col gap-1">
            <span className={`${pill} text-center ${codeIsOn(p) ? "bg-emerald-500" : "bg-gray-600"}`}>
              {t("bonus2.promo.code")}: {codeIsOn(p) ? t("bonus2.promo.on") : t("bonus2.promo.off")}
            </span>
            <span className={`${pill} text-center ${bonus.className}`}>{t(bonus.label)}</span>
            {!(codeIsOn(p) && p.active && !p.archived) && <span className="text-xs text-amber-300">{t("bonus2.promo.notRedeemable")}</span>}
          </div>
        )
      }
    },
    {
      header: t("bonus2.promo.thActions"),
      align: "right",
      width: "32%",
      render: (p) => (
        <div className="flex gap-2 justify-end">
          <button onClick={() => onViewUsage(p)} className={`${action} bg-blue-600 hover:bg-blue-700`}>
            {t("bonus2.promo.usage")}
          </button>
          {!p.codeArchived && (
            <button
              onClick={() => onToggle(p, !p.codeActive)}
              className={`${action} ${p.codeActive ? "bg-rose-500 hover:bg-rose-700" : "bg-green hover:bg-emerald-700"}`}
            >
              {t(p.codeActive ? "bonus2.promo.deactivate" : "bonus2.promo.reactivate")}
            </button>
          )}
          <button onClick={() => onArchive(p, !p.codeArchived)} className={`${action} bg-slate-600 hover:bg-slate-700`}>
            {t(p.codeArchived ? "bonus2.promo.unarchive" : "bonus2.promo.archive")}
          </button>
          {/* Offered on every row: the backend refuses a code with redemptions and the caller
              points the operator at Archive, so this only ever removes a typo or a test code. */}
          <button onClick={() => onDelete(p)} className={`${action} bg-rose-600 hover:bg-rose-700`}>
            {t("bonus2.promo.delete")}
          </button>
        </div>
      )
    }
  ]

  if (promoCodes.length === 0) {
    return <div className="text-center text-gray-400 py-12">{t("bonus2.promo.noPromoCodesFound")}</div>
  }

  const toggle = (id: string) => setOpenIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]))

  return (
    <div className="space-y-4">
      {visible.map((rows) => {
        const bonusName = getDisplayName(rows[0].name)
        const bonusId = rows[0].bonus_id
        const open = openIds.includes(bonusId)
        return (
          <div key={bonusId} className="bg-[#1A1A2E] rounded-lg overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-700">
              {/* Only the name toggles — a nested button inside a clickable row swallows the export. */}
              <button
                type="button"
                onClick={() => toggle(bonusId)}
                aria-expanded={open}
                className="flex items-center gap-2 text-white font-semibold transition-colors hover:text-[rgba(169,78,206,1)]"
              >
                <span className="text-xs text-gray-400">{open ? "▼" : "▶"}</span>
                {bonusName} <span className="text-gray-400 font-normal">({t("bonus2.promo.codeCount", { count: rows.length })})</span>
              </button>
              <button
                onClick={() => downloadPromoCodeRowsCsv(rows, bonusName)}
                className="px-3 py-1 text-xs bg-transparent text-[rgba(169,78,206,1)] border border-[rgba(169,78,206,0.8)] rounded transition-all hover:bg-[rgba(169,78,206,0.7)] hover:text-white"
              >
                {t("bonus2.promo.exportCsv")}
              </button>
            </div>
            {open && <DataTable rows={rows} columns={columns} rowKey={(p) => p.id} />}
          </div>
        )
      })}
      {totalPages > 1 && <TablePagination page={safePage} totalPages={totalPages} onJump={setPage} />}
    </div>
  )
}
